// src/styles/theme.ts
import { createTheme, Theme, alpha, PaletteOptions } from "@mui/material/styles";
import {
  buttons,
  colors,
  createThemeColors,
  fill,
  getColorByMode,
  semantic,
} from "./designTokens";

const getButtonRadius = (): number => Number.parseFloat(buttons.borderRadius.medium);
const getDividerColor = (mode: "dark" | "light"): string =>
  alpha(getColorByMode(colors.common[100], mode), 0.08);
const alphaBaseByMode = (mode: "dark" | "light"): string =>
  mode === "light" ? "#000" : "#FFF";

export const tokens = {
  brand: {
    primary: colors.primary[50].dark,
    secondary: colors.grey[50].dark,
  },
  success: colors.green[50].dark,
  warning: "#FFB547",
  error: colors.red[50].dark,
  info: colors.cyan[51].dark,
  dark: {
    bg: fill.background.grey20.dark,
    paper: fill.background.grey30.dark,
    text: fill.text.grey90.dark,
    textSecondary: fill.text.grey80.dark,
    divider: getDividerColor("dark"),
  },
  light: {
    bg: fill.background.grey20.light,
    paper: fill.background.grey30.light,
    text: fill.text.grey90.light,
    textSecondary: fill.text.grey80.light,
    divider: getDividerColor("light"),
  },
};

export const buildTheme = (mode: "dark" | "light" = "dark"): Theme => {
  const palette = createThemeColors(mode) as PaletteOptions;
  const buttonRadius = getButtonRadius();

  return createTheme({
    palette: {
      ...palette,
      warning: { main: tokens.warning },
      success: { main: tokens.success },
      info: { main: tokens.info },
      divider: mode === "light" ? tokens.light.divider : tokens.dark.divider,
      action: {
        // Neutral gray hover (mode-aware): subtle gray overlay instead of primary color
        hover:
          mode === "light"
            ? alpha(colors.common[100].light, 0.06)
            : alpha(colors.common[100].dark, 0.06),
      },
    },
    shape: { borderRadius: buttonRadius },
    typography: {
      fontFamily: `'Roboto-regular', 'Roboto-bold', 'Inter', 'Pretendard', system-ui, -apple-system, sans-serif`,
      h6: { fontWeight: 600 },
    },
    components: {
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: buttonRadius,
            fontWeight: 600,
          },
          containedPrimary: ({ theme }) => ({
            // Neutral gray background for default(primary) buttons
            backgroundColor:
              semantic.button.containedNeutral.background.default[
                theme.palette.mode
              ],
            color: semantic.button.containedNeutral.text[theme.palette.mode],
            boxShadow: "none",
            "&:hover": {
              backgroundColor:
                semantic.button.containedNeutral.background.hover[
                  theme.palette.mode
                ],
              boxShadow: "none",
            },
            "&:active": {
              backgroundColor:
                semantic.button.containedNeutral.background.active[
                  theme.palette.mode
                ],
            },
          }),
          outlinedPrimary: ({ theme }) => ({
            color: semantic.button.outlinedNeutral.text[theme.palette.mode],
            borderColor:
              semantic.button.outlinedNeutral.border[theme.palette.mode],
            "&:hover": {
              borderColor:
                semantic.button.outlinedNeutral.hoverBorder[
                  theme.palette.mode
                ],
              backgroundColor: alpha(
                alphaBaseByMode(theme.palette.mode),
                semantic.button.outlinedNeutral.hoverBackgroundAlpha[
                  theme.palette.mode
                ],
              ),
            },
          }),
          textPrimary: ({ theme }) => ({
            color: semantic.button.textNeutral.text[theme.palette.mode],
            "&:hover": {
              backgroundColor: alpha(
                alphaBaseByMode(theme.palette.mode),
                semantic.button.textNeutral.hoverBackgroundAlpha[
                  theme.palette.mode
                ],
              ),
            },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            minWidth: 120,
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: 8,
            boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
    },
  });
};
